// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Admin sign-in  (route /welcome/staff — "Staff sign in", linked from the login page; two-panel console gateway)
// After the Command Ops login mockup: a brand panel (what the console covers, how sign-in is secured) beside the sign-in form. The form is the
// REAL admin door: username (or work e-mail) + password with a show/hide toggle and "keep me signed in on this device" (Firebase local vs
// session persistence), or "Sign in with fingerprint" (WebAuthn passkey, no username). A successful sign-in must carry a staff role claim,
// else the session is dropped and a generic message is shown (never whether the username exists). Staff then land on /admin (the Command Ops
// dashboard). Customers still sign in with an SMS code on /welcome.
// Deliberately NOT here (no system behind it): the role-selector tabs (the role is a server-side claim, not a choice), company SSO
// (Okta / Google Workspace — shown as a disabled "coming soon" button), YubiKey/FIDO2 "enforced" (fingerprints are optional), IoT temperature /
// uptime / fleet-radar tiles, ISO 22000 / ISO 27001 / FSSAI badges, a session IP, a hotline number, "SecOps Guard v4.12", "@zenfresh.in secured".
// Neighbours: utils/staffAuth, components/AuthProvider (signInStaff / signInWithToken), pages/admin/AdminShell, pages/Welcome.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowRight, ClipboardList, CircleAlert, Eye, EyeOff, Fingerprint, Hourglass, KeyRound, Loader2, LockKeyhole, MessageSquareWarning, Shield, ShieldCheck, Trash2, Anchor } from "lucide-react";
import PageTransition from "../components/PageTransition";
import { CONTACT_EMAIL } from "../data/legalContact";
import useAuth from "../hooks/useAuth";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { ApiError } from "../utils/api";
import { fingerprintCustomToken, passkeysSupported } from "../utils/staffAuth";

const field = "w-full min-h-12 rounded-lg bg-ocean-700/5 pl-11 pr-11 text-base text-ocean-950 ring-1 ring-transparent focus:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 dark:bg-white/5 dark:text-foam-50 dark:focus:bg-ocean-950";

// Firebase codes that all mean "these details didn't work" — shown as one message so nothing leaks.
const BAD_LOGIN = new Set(["auth/invalid-credential", "auth/wrong-password", "auth/user-not-found", "auth/invalid-email", "auth/missing-password"]);

const COVERS = [
  { icon: ClipboardList, text: "Order management: confirm, prepare, dispatch and deliver" },
  { icon: Anchor, text: "Today's Daily Catch landings and packs left" },
  { icon: MessageSquareWarning, text: "Complaint review with wallet credit decisions" },
  { icon: Trash2, text: "Customer data-erasure requests" },
];

export default function StaffLogin() {
  useDocumentTitle("Staff sign in");
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  const auth = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [reveal, setReveal] = useState(false);
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState<"password" | "fingerprint" | null>(null);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const errorRef = useRef<HTMLParagraphElement>(null);
  const canFingerprint = passkeysSupported();

  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);

  if (auth.status === "signed-in" && auth.user?.role) return <Navigate to="/admin" replace />;

  const fail = (caught: unknown) => {
    setVerified(false);
    const code = typeof caught === "object" && caught !== null && "code" in caught ? String((caught as { code: unknown }).code) : "";
    if (caught instanceof Error && caught.message === "NOT_STAFF") setError("That account doesn't have admin access.");
    else if (BAD_LOGIN.has(code)) setError("That username or password isn't right.");
    else if (code === "auth/too-many-requests") setError("Too many attempts. Please wait a few minutes and try again.");
    else if (code === "auth/user-disabled") setError("This account has been disabled.");
    else if (caught instanceof ApiError || caught instanceof Error) setError(caught.message);
    else setError("We couldn't sign you in. Please try again.");
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!username.trim() || !password) return setError("Enter your username and password.");
    setBusy("password");
    setError(null);
    try {
      await auth.signInStaff(username, password, remember);
      setVerified(true); // the guard redirects to /admin as soon as the session lands
    } catch (caught) {
      fail(caught);
    } finally {
      setBusy(null);
    }
  };

  const fingerprint = async () => {
    setBusy("fingerprint");
    setError(null);
    try {
      await auth.signInWithToken(await fingerprintCustomToken(), remember);
      setVerified(true);
    } catch (caught) {
      fail(caught);
    } finally {
      setBusy(null);
    }
  };

  return (
    <PageTransition>
      <div className="flex min-h-screen items-center justify-center bg-foam-50 p-4 pt-12 text-ocean-950 dark:bg-ocean-950 dark:text-foam-50">
        <div className="grid w-full max-w-6xl overflow-hidden rounded-2xl bg-white shadow-xl shadow-ocean-900/10 dark:bg-ocean-900 lg:grid-cols-12">
          {/* Brand panel */}
          <aside aria-label="About the console" className="relative flex flex-col justify-between gap-8 overflow-hidden bg-gradient-to-br from-ocean-800 to-ocean-950 p-7 text-foam-50 lg:col-span-5 lg:p-9">
            <span aria-hidden="true" className="pointer-events-none absolute -left-24 -top-24 h-80 w-80 rounded-full bg-teal-600/30 blur-3xl" />
            <div className="relative">
              <div className="flex items-center gap-3">
                <span aria-hidden="true" className="flex h-10 w-10 items-center justify-center rounded-lg bg-white text-ocean-800 shadow-md"><Anchor className="h-6 w-6" /></span>
                <div><p className="font-display text-lg font-bold leading-tight">ZenFresh</p><p className="text-[11px] font-bold uppercase tracking-widest text-foam-100/80">Command Ops</p></div>
              </div>
              <h2 className="mt-8 font-display text-2xl font-bold leading-snug">Everything the team needs to run today&apos;s orders.</h2>
              <ul className="mt-5 space-y-2">
                {COVERS.map(({ icon: Icon, text }) => (
                  <li key={text} className="flex items-center gap-3 rounded-lg bg-white/10 p-3 text-sm"><Icon className="h-5 w-5 shrink-0 text-teal-300" aria-hidden="true" /> {text}</li>
                ))}
              </ul>
            </div>
            <div className="relative space-y-2 border-t border-white/15 pt-5 text-sm text-foam-100/90">
              <p className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-teal-300" aria-hidden="true" /> Access is by role: admin, editor or viewer.</p>
              <p className="flex items-center gap-2"><Fingerprint className="h-5 w-5 text-teal-300" aria-hidden="true" /> Sign in with a fingerprint once you&apos;ve set one up.</p>
            </div>
          </aside>

          {/* Sign-in panel */}
          <section aria-label="Sign in" className="flex flex-col justify-between gap-6 p-7 lg:col-span-7 lg:p-10">
            <div>
              <p className="inline-flex items-center gap-2 rounded-full bg-ocean-700/10 px-3 py-1 text-[11px] font-bold text-ocean-900 dark:bg-white/10 dark:text-foam-50"><Shield className="h-4 w-4" aria-hidden="true" /> Internal operations gateway</p>
              <h1 className="mt-4 font-display text-3xl font-bold tracking-tight">Staff sign in</h1>
              <p className="mt-1 text-sm text-ocean-800/85 dark:text-foam-100/80">Enter your ZenFresh staff credentials to open the operations dashboard. Customers sign in with a mobile number instead.</p>

              <form onSubmit={(event) => void submit(event)} noValidate className="mt-6 space-y-4" aria-describedby={error ? "staff-error" : undefined}>
                <div>
                  <label htmlFor="staff-username" className="text-sm font-bold">Username or work e-mail</label>
                  <div className="relative mt-1">
                    <KeyRound aria-hidden="true" className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-ocean-800/60 dark:text-foam-100/60" />
                    <input id="staff-username" name="username" autoComplete="username" autoCapitalize="none" spellCheck={false} value={username} onChange={(event) => setUsername(event.target.value)} className={field} />
                  </div>
                </div>
                <div>
                  <label htmlFor="staff-password" className="text-sm font-bold">Password</label>
                  <div className="relative mt-1">
                    <LockKeyhole aria-hidden="true" className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-ocean-800/60 dark:text-foam-100/60" />
                    <input id="staff-password" name="password" type={reveal ? "text" : "password"} autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} className={field} />
                    <button type="button" onClick={() => setReveal((v) => !v)} aria-pressed={reveal} aria-label="Reveal what you typed" className="absolute right-1 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full text-ocean-800/70 hover:text-ocean-950 focus-visible:outline-2 focus-visible:outline-teal-600 dark:text-foam-100/70">
                      {reveal ? <EyeOff className="h-5 w-5" aria-hidden="true" /> : <Eye className="h-5 w-5" aria-hidden="true" />}
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <label className="inline-flex min-h-11 cursor-pointer items-center gap-2 text-sm"><input type="checkbox" checked={remember} onChange={(event) => setRemember(event.target.checked)} className="h-4 w-4 accent-ocean-800" /> Keep me signed in on this device</label>
                  <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">Forgot your password? Ask an administrator to reset it.</p>
                </div>
                <p id="staff-error" ref={errorRef} tabIndex={-1} role="alert" className="flex min-h-5 items-start gap-1.5 text-sm text-coral-600 outline-none dark:text-coral-300">
                  {error && <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />}
                  {error}
                </p>
                <p role="status" className="sr-only">{verified ? "Identity verified. Opening the operations dashboard." : ""}</p>
                <button type="submit" disabled={busy !== null || verified} className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-lg bg-ocean-800 px-6 text-base font-bold text-white shadow-md hover:bg-ocean-700 disabled:opacity-70">
                  {busy === "password" ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : null}
                  {verified ? "Identity verified — opening the dashboard…" : <>Sign in <ArrowRight className="h-5 w-5" aria-hidden="true" /></>}
                </button>
              </form>

              <div className="relative my-6 text-center text-[11px] font-bold uppercase tracking-wider text-ocean-800/70 dark:text-foam-100/65">
                <span aria-hidden="true" className="absolute inset-x-0 top-1/2 h-px bg-ocean-900/10 dark:bg-white/10" />
                <span className="relative bg-white px-3 dark:bg-ocean-900">Or</span>
              </div>

              <div className="space-y-2">
                <button type="button" onClick={() => void fingerprint()} disabled={busy !== null || verified || !canFingerprint} className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-lg bg-teal-700 px-6 text-base font-bold text-white shadow-md hover:opacity-90 disabled:opacity-50">
                  {busy === "fingerprint" ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : <Fingerprint className="h-5 w-5" aria-hidden="true" />} Sign in with fingerprint
                </button>
                <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">{canFingerprint ? "Optional. Set one up after signing in (Security), then use it here — no username needed." : "This browser doesn't support fingerprint sign-in."}</p>
                <button type="button" disabled title="Coming soon" className="inline-flex min-h-11 w-full cursor-not-allowed items-center justify-center gap-2 rounded-lg bg-ocean-700/10 px-4 text-sm font-bold opacity-60 dark:bg-white/10">
                  <Hourglass className="h-4 w-4" aria-hidden="true" /> Company single sign-on <span className="rounded-full bg-coral-500/20 px-1.5 text-[10px] uppercase text-coral-600 dark:text-coral-300">Coming soon</span>
                </button>
              </div>
            </div>

            <div className="flex flex-col justify-between gap-1 border-t border-ocean-900/10 pt-4 text-xs text-ocean-800/80 dark:border-white/10 dark:text-foam-100/75 sm:flex-row">
              <span>Trouble getting in? <a href={`mailto:${CONTACT_EMAIL}`} className="font-bold underline">Contact an administrator</a>.</span>
              <Link to="/welcome" className="font-bold underline">Back to customer sign in</Link>
            </div>
          </section>
        </div>
      </div>
    </PageTransition>
  );
}
